interface RequestInfo {
    promise: Promise<any>;
    timestamp: Date;
    inProgress: boolean;
    expiryMinutes: number;
}

interface RequestManagerOptions {
    expiryMinutes?: number;
    retryDelay?: number;
    maxRetries?: number;
}

// Module-scoped state
const requests: Map<string, RequestInfo> = new Map();
const defaultOptions: RequestManagerOptions = {
    expiryMinutes: 2,
    retryDelay: 2000, // 2 seconds
    maxRetries: 3,
};

/**
 * Configure the request throttle manager with custom options
 * @param options The configuration options
 */
export const configure = (options: RequestManagerOptions): void => {
    // TODO: FUNCTION-MUTATION - Function mutates module-level state (defaultOptions) without protection against concurrent modifications. Consider using immutable patterns or locks.
    if (options.expiryMinutes !== undefined) defaultOptions.expiryMinutes = options.expiryMinutes;
    if (options.retryDelay !== undefined) defaultOptions.retryDelay = options.retryDelay;
    if (options.maxRetries !== undefined) defaultOptions.maxRetries = options.maxRetries;
};

/**
 * Execute a request with throttling and caching
 * @param key Unique key to identify the request (typically endpoint + query params)
 * @param request The request function that returns a promise
 * @returns Promise with the request result
 */
export const execute = async <T>(key: string, request: () => Promise<T>): Promise<T> => {
    const existingRequest = requests.get(key);

    // If there's an existing request in progress, wait for it to complete
    if (existingRequest && existingRequest.inProgress) {
        console.log(`RequestThrottleManager: Request in progress, waiting for ${key}`);
        return existingRequest.promise as Promise<T>;
    }

    // Create a new request promise
    const promise = (async () => {
        try {
            const result = await request();
            return result;
        } finally {
            // Remove the request from the map once completed
            requests.delete(key);
        }
    })();

    // Store the request as in progress
    requests.set(key, {
        promise,
        timestamp: new Date(), // Timestamp is irrelevant now but kept for compatibility
        inProgress: true,
        // TODO: LOGIC-ERROR - Comment says "Expiry is irrelevant now" but still storing expiryMinutes field. If truly irrelevant, should remove this field and update the interface
        expiryMinutes: 0, // Expiry is irrelevant now
    });

    return promise;
};

/**
 * Clear a cached request by key
 * @param key The request key to clear
 */
export const clear = (key: string): void => {
    requests.delete(key);
};

/**
 * Clear all cached requests
 */
export const clearAll = (): void => {
    requests.clear();
};

/**
 * Check if a request is currently in progress
 * @param key The request key to check
 * @returns boolean indicating if the request is in progress
 */
export const isRequestInProgress = (key: string): boolean => {
    const request = requests.get(key);
    return request ? request.inProgress : false;
};

// Export as a named object for backward compatibility with the previous class-based implementation
export const requestThrottleManager = {
    execute,
    clear,
    clearAll,
    isRequestInProgress,
    configure,
};

export default requestThrottleManager;
